import { ApiService } from './ApiService';

/**
 * Interface para configurações sociais
 */
export interface SocialSettings {
  id: number;
  facebook_url?: string;
  twitter_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  github_url?: string;
  privacy_policy_content?: string;
  terms_content?: string;
  help_content?: string;
  whatsapp_contact?: string;
  contact_email?: string;
  app_download_url?: string;
  youtube_video_url?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interface para atualização parcial de configurações
 */
export type SocialSettingsUpdate = Partial<Omit<SocialSettings, 'id' | 'created_at' | 'updated_at'>>;

/**
 * Serviço para gerenciar configurações sociais
 * Estende ApiService para herdar funcionalidades de HTTP
 */
export class SocialService extends ApiService {
  private static instance: SocialService;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  /**
   * Singleton pattern
   */
  public static getInstance(): SocialService {
    if (!SocialService.instance) {
      SocialService.instance = new SocialService();
    }
    return SocialService.instance;
  }

  /**
   * Verifica se o cache é válido
   */
  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;

    const now = Date.now();
    return now - cached.timestamp < this.CACHE_TTL;
  }

  /**
   * Obtém dados do cache
   */
  private getFromCache<T>(key: string): T | null {
    if (this.isCacheValid(key)) {
      return this.cache.get(key)!.data as T;
    }
    return null;
  }

  /**
   * Salva dados no cache
   */
  private saveToCache<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Limpa o cache
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Limpa cache de uma chave específica
   */
  public clearCacheKey(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Obtém configurações sociais
   * @param useCache - Se deve usar cache (padrão: true)
   * @returns Configurações sociais
   */
  public async getSettings(useCache: boolean = true): Promise<SocialSettings> {
    const cacheKey = 'social-settings';

    // Tentar obter do cache primeiro
    if (useCache) {
      const cached = this.getFromCache<SocialSettings>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Buscar da API
    const settings = await this.get<SocialSettings>('/social/settings');

    // Salvar no cache
    this.saveToCache(cacheKey, settings);

    return settings;
  }

  /**
   * Atualiza configurações sociais
   * @param settings - Configurações a serem atualizadas
   * @returns Configurações atualizadas
   */
  public async updateSettings(settings: SocialSettingsUpdate): Promise<SocialSettings> {
    const updatedSettings = await this.put<SocialSettings>('/social/settings', settings);

    // Atualizar cache
    this.saveToCache('social-settings', updatedSettings);

    return updatedSettings;
  }

  /**
   * Helpers para WhatsApp
   */

  /**
   * Valida número de WhatsApp
   * @param whatsapp - Número de WhatsApp
   * @returns true se válido
   */
  public isWhatsAppValid(whatsapp?: string): boolean {
    if (!whatsapp) return false;
    const cleaned = whatsapp.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  }

  /**
   * Formata link do WhatsApp
   * @param whatsapp - Número de WhatsApp
   * @param message - Mensagem padrão (opcional)
   * @returns URL do WhatsApp ou null se inválido
   */
  public formatWhatsAppLink(whatsapp?: string, message?: string): string | null {
    if (!whatsapp || !this.isWhatsAppValid(whatsapp)) return null;

    const cleaned = whatsapp.replace(/\D/g, '');
    const withCountryCode = cleaned.length === 11 ? `55${cleaned}` : cleaned;
    const encodedMessage = encodeURIComponent(message || 'Olá! Gostaria de mais informações.');

    return `https://wa.me/${withCountryCode}?text=${encodedMessage}`;
  }

  /**
   * Obtém contato do WhatsApp das configurações
   * @returns Número de WhatsApp ou null
   */
  public async getWhatsAppContact(): Promise<string | null> {
    const settings = await this.getSettings();
    return settings.whatsapp_contact || null;
  }

  /**
   * Obtém email de contato das configurações
   * @returns Email de contato ou null
   */
  public async getContactEmail(): Promise<string | null> {
    const settings = await this.getSettings();
    return settings.contact_email || null;
  }

  /**
   * Obtém URL de download do app
   * @returns URL de download ou null
   */
  public async getAppDownloadUrl(): Promise<string | null> {
    const settings = await this.getSettings();
    return settings.app_download_url || null;
  }

  /**
   * Obtém URL do vídeo do YouTube
   * @returns URL do vídeo ou null
   */
  public async getYouTubeVideoUrl(): Promise<string | null> {
    const settings = await this.getSettings();
    return settings.youtube_video_url || null;
  }

  /**
   * Obtém todas as URLs de redes sociais
   * @returns Objeto com URLs de redes sociais
   */
  public async getSocialMediaUrls(): Promise<{
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
    github?: string;
  }> {
    const settings = await this.getSettings();

    return {
      facebook: settings.facebook_url,
      twitter: settings.twitter_url,
      instagram: settings.instagram_url,
      linkedin: settings.linkedin_url,
      youtube: settings.youtube_url,
      github: settings.github_url,
    };
  }

  /**
   * Valida se uma URL de rede social é válida
   * @param url - URL a ser validada
   * @returns true se válida
   */
  public isSocialMediaUrlValid(url?: string): boolean {
    if (!url) return false;

    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Atualiza apenas URLs de redes sociais
   * @param urls - URLs a serem atualizadas
   * @returns Configurações atualizadas
   */
  public async updateSocialMediaUrls(urls: {
    facebook_url?: string;
    twitter_url?: string;
    instagram_url?: string;
    linkedin_url?: string;
    youtube_url?: string;
    github_url?: string;
  }): Promise<SocialSettings> {
    return this.updateSettings(urls);
  }

  /**
   * Atualiza informações de contato
   * @param contact - Informações de contato
   * @returns Configurações atualizadas
   */
  public async updateContactInfo(contact: {
    whatsapp_contact?: string;
    contact_email?: string;
  }): Promise<SocialSettings> {
    return this.updateSettings(contact);
  }
}

// Instância singleton do serviço
export const socialService = SocialService.getInstance();
